import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import GlobalStyles from './styles/GlobalStyles';
import ErrorBoundary from './components/ErrorBoundary';
import { ContentProvider } from './context/ContentContext';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const About = lazy(() => import('./pages/About'));
const Careers = lazy(() => import('./pages/Careers'));
const Contact = lazy(() => import('./pages/Contact'));
const CMSLogin = lazy(() => import('./pages/CMSLogin'));
const CMSLoginSupabase = lazy(() => import('./pages/CMSLoginSupabase'));
const CMSAdmin = lazy(() => import('./pages/CMSAdmin'));

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
  padding-top: 70px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 140px);
  font-size: 1.5rem;
  color: var(--primary-color);
`;

const ScrollToTop = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 55px;
  height: 55px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  opacity: ${props => props.$visible ? '1' : '0'};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transform: ${props => props.$visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.8)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  box-shadow: 0 4px 20px rgba(255, 87, 34, 0.4);
  font-weight: bold;
  line-height: 1;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 8px 30px rgba(255, 87, 34, 0.6);
  }

  &:active {
    transform: translateY(-3px) scale(0.98);
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: inherit;
    z-index: -1;
    opacity: 0;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.1);
      opacity: 0;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

function ScrollToTopButton() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <ScrollToTop 
      $visible={isVisible} 
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      ↑
    </ScrollToTop>
  );
}

function ScrollToTopOnMount() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ContentProvider>
          <GlobalStyles />
          <ScrollToTopOnMount />
          <AppWrapper>
            <AppContent />
          </AppWrapper>
        </ContentProvider>
      </Router>
    </ErrorBoundary>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdminPage && <Navigation />}
      <Main>
        <Suspense fallback={
          <LoadingWrapper>
            <div>Loading...</div>
          </LoadingWrapper>
        }>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin/login" element={<CMSLoginSupabase />} />
            <Route path="/admin/login/basic" element={<CMSLogin />} />
            <Route path="/admin" element={<CMSAdmin />} />
          </Routes>
        </Suspense>
      </Main>
      {!isAdminPage && <Footer />}
      {!isAdminPage && <ScrollToTopButton />}
    </>
  );
}

export default App;
